import { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Link, Typography } from '@mui/material';

/**
 * Human-readable reference for the public REST API, at `/api-docs`.
 *
 * A ROUTE, not a static file in `public/`. CloudFront's `defaultRootObject`
 * applies only to `/`, so a file at `public/api-docs/index.html` would be
 * reachable only at `/api-docs/index.html` — a request to `/api-docs` 404s in S3
 * and gets rewritten to `/index.html` (see infra/lib/frontend-stack.ts). Routing
 * it here is what makes the requested URL, `https://app.traaker.ai/api-docs`,
 * actually work, with no CloudFront function and no infra change.
 *
 * UNAUTHENTICATED on purpose — mounted outside `ProtectedRoute`. A developer
 * evaluating the API has no account yet, and an interface description is not
 * customer data. The spec it renders is the one the API serves, so the page can
 * never describe a surface the deployment doesn't have.
 *
 * Redoc is loaded from a CDN at runtime rather than bundled: it is ~1 MB, and
 * adding it to the main chunk would slow every app page load for a page almost
 * no signed-in user opens.
 */

const REDOC_SRC = 'https://cdn.redoc.ly/redoc/v2.1.5/bundles/redoc.standalone.js';

function specUrl(): string {
  const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.traaker.ai/api';
  return `${apiUrl.replace(/\/$/, '')}/public/v1/openapi.json`;
}

export default function ApiDocs() {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Traaker API — Reference';
    let cancelled = false;

    // `Redoc.init` rather than the <redoc> custom element: the element would
    // need a JSX IntrinsicElements augmentation, and init gives us the container
    // ref and the error callback directly.
    const render = () => {
      if (cancelled || !container.current) return;
      const Redoc = (window as any).Redoc;
      if (!Redoc) {
        setStatus('error');
        return;
      }
      Redoc.init(specUrl(), { hideDownloadButton: false, expandResponses: '200' }, container.current);
      setStatus('ready');
    };

    // Re-entrant: React 18 StrictMode mounts effects twice in dev, and the user
    // can navigate away and back. Reuse the tag rather than appending a second
    // ~1 MB script.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${REDOC_SRC}"]`);
    if (existing?.dataset.loaded === 'true') {
      render();
      return () => { cancelled = true; };
    }

    const script = existing ?? document.createElement('script');
    const onLoad = () => {
      script.dataset.loaded = 'true';
      render();
    };
    const onError = () => setStatus('error');
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    if (!existing) {
      script.src = REDOC_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    return () => {
      cancelled = true;
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };
  }, []);

  if (status === 'error') {
    return (
      <Box sx={{ p: 4, maxWidth: 720, mx: 'auto' }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Traaker API
        </Typography>
        {/* The spec is the source of truth, so a failed viewer must still leave
            the reader a way to read it. */}
        <Alert severity="warning">
          The interactive reference could not be loaded. The OpenAPI document is
          available directly at{' '}
          <Link href={specUrl()} target="_blank" rel="noopener">
            {specUrl()}
          </Link>
          .
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {status === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {/* Always mounted: Redoc.init needs the node to exist when the script's
          load event fires, so this cannot be conditional on `status`. */}
      <div ref={container} />
    </Box>
  );
}
